import React, { FC, useEffect, useRef } from 'react';
import {
  CommsCheckResult,
  XrdlgData,
  XrdlgResponseData,
  XrinvData,
  XrinvResponseData,
  XritmResponseData,
} from '../../types/comms-check';
import { buildXrdlgRequest, buildXrinvRequest, buildXritmRequest, getAddress } from '../util';
import commsCheckInitialData, { useCommsCheckContext } from '../../context/CommsCheckContext';
import {
  getXrdlgData,
  getXrinvData,
  getXritmData,
  mergeXrdlgIntoXrinv,
  proxy500L1error,
  proxy500L2error,
  unexpectedL1Error,
  unexpectedL2Error,
} from './util';
import { WaitingForResponse } from './WaitingForResponse';
import { CommsCheckSuccessResult } from './CommsCheckSuccessResult';
import { CommsCheckFailResult } from './CommsCheckFailResult';
import { atomProxyCommsCheckClient } from '../../api/atomProxyClient';
import { buildPostRequest } from '../../api/apiClient';

interface CommsCheckProps {
  mpxn: string;
  msn: string;
  retryCommsCheck: number;
  reportCommsCheck: (data: CommsCheckResult) => Promise<void>;
}

export const CommsCheck: FC<CommsCheckProps> = ({ mpxn, msn, reportCommsCheck, retryCommsCheck = 0 }) => {
  const [statusScreen, setStatusScreen] = React.useState<'SUCCESS' | 'FAIL' | 'LOAD' | ''>('');
  const { commsCheckResultContext, setCommsCheckResultContext } = useCommsCheckContext();
  const [deviceLogLength, setDeviceLogLength] = React.useState<number>(0);
  const xrinvData = useRef<XrinvResponseData>({ data: [], address: null, state: null });
  const xritmData = useRef<XritmResponseData>({
    data: [],
    state: null,
    msn: '',
  });
  const xrdlgData = useRef<XrdlgResponseData>({ data: [], state: null });

  useEffect(() => {
    init();
    sendSingleGenerateXmlXrinvXritm();
  }, [retryCommsCheck]);

  const getXmlInitialTimeout = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_INITIAL_TIMEOUT}`, 10);
  const getXmlTimeout = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_TIMEOUT}`, 10);
  const maxRetries = parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_MAX_RETRIES}`, 10);

  let retriesXrinv = 0;
  let retriesXritm = 0;
  let retriesXrdlg = 0;
  // This should always be updated through the updateProgress function to avoid inconsistent state
  const inProgress = useRef(false);

  function init() {
    retriesXrinv = 0;
    retriesXritm = 0;
    retriesXrdlg = 0;
    updateProgress(false);
    setCommsCheckResultContext(commsCheckInitialData);
    clearCommsCheckData();
    setStatusScreen('');
  }

  /**
   * Updates both, the inProgress variable used in the JS part of the component and the
   * automationInProgress state variable used in the HTML part of the component.
   *
   * These two variable should only be updated through this function to avoid inconsistent state.
   *
   * @param state - the new state
   */
  function updateProgress(state: boolean) {
    inProgress.current = state;
  }

  function clearCommsCheckData() {
    xrinvData.current = { data: [], address: null, state: null };
    xritmData.current = { data: [], state: null, msn: '' };
    xrdlgData.current = { data: [], state: null };
  }

  const sendSingleGenerateXmlXrinvXritm = () => {
    const request = buildXrinvRequest(mpxn);
    updateProgress(true);
    setStatusScreen('LOAD');
    atomProxyCommsCheckClient
      .generateXmlXrinv(request)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          inProgress.current = false;
          checkCompleted(proxy500L1error);
        }
        return httpResponse.json();
      })
      .then((l1Response) => {
        if (inProgress.current) {
          setTimeout(() => sendSingleGetXmlXrinv(l1Response.atomL1response, mpxn), getXmlInitialTimeout);
        }
      })
      .catch(() => {
        inProgress.current = false;
        checkCompleted(unexpectedL1Error);
      });

    const xritmRequest = buildXritmRequest(mpxn, msn);
    atomProxyCommsCheckClient
      .generateXmlXritm(xritmRequest)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          inProgress.current = false;
          checkCompleted(proxy500L1error);
        }
        return httpResponse.json();
      })
      .then((l1Response) => {
        if (inProgress.current) {
          setTimeout(() => sendSingleGetXmlXritm(l1Response.atomL1response), getXmlInitialTimeout);
        }
      })
      .catch(() => {
        inProgress.current = false;
        checkCompleted(unexpectedL1Error);
      });
  };

  const sendSingleGenerateXmlXrdlg = (request: any) => {
    atomProxyCommsCheckClient
      .generateXmlXrdlg(request)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          inProgress.current = false;
          checkCompleted(proxy500L1error);
        }
        return httpResponse.json();
      })
      .then((l1Response) => {
        if (inProgress.current) {
          setTimeout(() => sendSingleGetXmlXrdlg(l1Response.atomL1response), getXmlInitialTimeout);
        }
      })
      .catch(() => {
        inProgress.current = false;
        checkCompleted(unexpectedL1Error);
      });
  };

  const sendSingleGetXmlXrinv = (data: any, mpxn: string) => {
    if (!inProgress.current) {
      return;
    }
    const xrinvGetXmlRequest = buildPostRequest(data);
    atomProxyCommsCheckClient
      .getXmlXrinv(xrinvGetXmlRequest)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          if (inProgress.current && maxRetries > retriesXrinv) {
            retriesXrinv += 1;
            setTimeout(() => sendSingleGetXmlXrinv(data, mpxn), getXmlTimeout);
          } else {
            inProgress.current = false;
            checkCompleted(proxy500L2error);
          }
        }
        return httpResponse.json();
      })
      .then((l2Response) => {
        if (l2Response.bolQueryState === 'FAIL') {
          xrinvData.current = {
            state: 'FAIL',
            address: null,
            data: [],
          };
          xrdlgData.current = {
            state: 'NOT_STARTED',
            data: [],
          };

          retriesXrinv = 0;
          checkCompleted({
            xrinv: xrinvData.current,
            xritm: xritmData.current,
            xrdlg: xrdlgData.current,
          });
        }

        if (l2Response.bolQueryState === 'PROCESSING') {
          if (inProgress.current && maxRetries > retriesXrinv) {
            retriesXrinv += 1;
            setTimeout(() => sendSingleGetXmlXrinv(data, mpxn), getXmlTimeout);
          } else {
            xrinvData.current = {
              state: l2Response.bolQueryState,
              address: null,
              data: getXrinvData(l2Response) || [],
            };
            xrdlgData.current = {
              state: 'FAIL',
              data: [],
            };

            retriesXrinv = 0;
            checkCompleted({
              xrinv: xrinvData.current,
              xritm: xritmData.current,
              xrdlg: xrdlgData.current,
            });
          }
        }

        if (l2Response.bolQueryState === 'SUCCESS') {
          xrinvData.current = {
            state: l2Response.bolQueryState,
            address: getAddress(l2Response),
            data: getXrinvData(l2Response),
          };

          const chfGuid = xrinvData.current.data.find((device) => device.deviceType === 'CHF')?.deviceId;
          const request = buildXrdlgRequest(mpxn, chfGuid);
          if (inProgress.current) {
            sendSingleGenerateXmlXrdlg(request);
          }

          retriesXrinv = 0;
        }
      })
      .catch(() => {
        inProgress.current = false;
        checkCompleted(unexpectedL2Error);
      });
  };

  const sendSingleGetXmlXritm = (data: any) => {
    if (!inProgress.current) {
      return;
    }
    const xritmGetXmlRequest = buildPostRequest(data);
    atomProxyCommsCheckClient
      .getXmlXritm(xritmGetXmlRequest)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          if (inProgress.current && maxRetries > retriesXritm) {
            retriesXritm += 1;
            setTimeout(() => sendSingleGetXmlXritm(data), getXmlTimeout);
          } else {
            inProgress.current = false;
            checkCompleted(proxy500L2error);
          }
        }
        return httpResponse.json();
      })
      .then((l2Response) => {
        if (l2Response.bolQueryState === 'FAIL') {
          xritmData.current = {
            state: 'FAIL',
            data: [],
            msn: msn,
          };

          retriesXritm = 0;
          checkCompleted({
            xrinv: xrinvData.current,
            xritm: xritmData.current,
            xrdlg: xrdlgData.current,
          });
        }

        if (l2Response.bolQueryState === 'PROCESSING') {
          if (inProgress.current && maxRetries > retriesXritm) {
            retriesXritm += 1;
            setTimeout(() => sendSingleGetXmlXritm(data), getXmlTimeout);
          } else {
            xritmData.current = {
              state: l2Response.bolQueryState,
              data: getXritmData(l2Response) || [],
              msn: msn,
            };

            retriesXritm = 0;
            checkCompleted({
              xrinv: xrinvData.current,
              xritm: xritmData.current,
              xrdlg: xrdlgData.current,
            });
          }
        }

        if (l2Response.bolQueryState === 'SUCCESS') {
          xritmData.current = {
            state: l2Response.bolQueryState,
            data: getXritmData(l2Response) || [],
            msn: l2Response.atomL2response['0']?.xml_string?.Body?.XRITM_OUT_02?.MSN,
          };

          retriesXritm = 0;
          checkCompleted({
            xrinv: xrinvData.current,
            xritm: xritmData.current,
            xrdlg: xrdlgData.current,
          });
        }
      })
      .catch(() => {
        inProgress.current = false;
        checkCompleted(unexpectedL2Error);
      });
  };

  const sendSingleGetXmlXrdlg = (data: any) => {
    if (!inProgress.current) {
      return;
    }
    const request = buildPostRequest(data);
    atomProxyCommsCheckClient
      .getXmlXrdlg(request)
      .then((httpResponse) => {
        if (httpResponse.status === 500) {
          if (inProgress.current && maxRetries > retriesXrdlg) {
            retriesXrdlg += 1;
            setTimeout(() => sendSingleGetXmlXrdlg(data), getXmlTimeout);
          } else {
            inProgress.current = false;
            checkCompleted(proxy500L2error);
          }
        }
        return httpResponse.json();
      })
      .then((response) => {
        if (response.bolQueryState === 'FAIL') {
          xrdlgData.current = {
            state: 'FAIL',
            data: [],
          };

          retriesXrdlg = 0;
          checkCompleted({
            xrinv: xrinvData.current,
            xritm: xritmData.current,
            xrdlg: xrdlgData.current,
          });
        }

        if (response.bolQueryState === 'PROCESSING') {
          if (inProgress.current && maxRetries > retriesXrdlg) {
            retriesXrdlg += 1;
            setTimeout(() => sendSingleGetXmlXrdlg(data), getXmlTimeout);
          } else {
            xrdlgData.current = {
              state: response.bolQueryState,
              data: [],
            };

            retriesXrdlg = 0;
            checkCompleted({
              xrinv: xrinvData.current,
              xritm: xritmData.current,
              xrdlg: xrdlgData.current,
            });
          }
        }

        if (response.bolQueryState === 'SUCCESS') {
          const allXrdlgResponseData = getXrdlgData(response);
          const deviceInventoryIds = xrinvData.current.data.map((device) => device.deviceId);
          const otherDevices = allXrdlgResponseData.filter((device: XrdlgData) => !deviceInventoryIds.includes(device.deviceId));

          // TODO: check why we add other devices here and not all devices
          xrdlgData.current = {
            state: response.bolQueryState,
            data: otherDevices,
          };

          xrinvData.current.data = mergeXrdlgIntoXrinv(xrinvData.current.data, allXrdlgResponseData);

          retriesXrdlg = 0;
          checkCompleted({
            xrinv: xrinvData.current,
            xritm: xritmData.current,
            xrdlg: xrdlgData.current,
          });
        }
      })
      .catch(() => {
        inProgress.current = false;
        checkCompleted(unexpectedL2Error);
      });
  };

  function checkCompleted(commsCheckResult: CommsCheckResult) {
    // Check if all three requests have been completed
    if (commsCheckResult.xrinv.state && commsCheckResult.xritm.state && commsCheckResult.xrdlg.state) {
      void reportCommsCheck(commsCheckResult);
      const filteredDevicesList = filterDevicesForEsmeGsme(commsCheckResult.xrinv.data);
      const isMetersActive = filteredDevicesList.every((device) => device.activeStatus === true);

      if (
        commsCheckResult.xrinv.state === 'SUCCESS' &&
        commsCheckResult.xritm.state === 'SUCCESS' &&
        commsCheckResult.xrdlg.state === 'SUCCESS' &&
        isMetersActive
      ) {
        setStatusScreen('SUCCESS');
        setCommsCheckResultContext(commsCheckResult);
        setDeviceLogLength(commsCheckResult.xrdlg.data.length);
        return;
      }

      setStatusScreen('FAIL');
      setCommsCheckResultContext(commsCheckResult);
    }
  }

  return (
    <div className="commsCheckContainer" id="commsCheckContainer" data-testid="comms-check-v2">
      {statusScreen === 'LOAD' && <WaitingForResponse />}
      {statusScreen === 'SUCCESS' && (
        <CommsCheckSuccessResult commsCheckData={commsCheckResultContext} deviceLogLength={deviceLogLength} />
      )}
      {statusScreen === 'FAIL' && (
        <CommsCheckFailResult
          failScreenData={buildFailScreenData(commsCheckResultContext, msn)}
          commsCheckData={commsCheckResultContext}
        />
      )}
    </div>
  );
};

export function filterDevicesForEsmeGsme(xrinvData: Array<XrinvData>): Array<XrinvData> {
  return xrinvData.filter((device) => device.deviceType === 'ESME' || device.deviceType === 'GSME');
}

export function buildChatData(data: CommsCheckResult, mpxn: string, msn: string) {
  const chf = data.xrinv.data.find((device) => device.deviceType === 'CHF')?.deviceId;
  const chfText = chf ? `CHF: ${chf}\n` : '';
  const deviceData = data.xrinv.data.map((invDevice) => {
    const deviceLct = invDevice.lastCommunicatedTime ? `LCT: ${invDevice.lastCommunicatedTime}\n\n` : '\n';

    return `${invDevice.deviceType}:
      Status: ${invDevice.deviceStatus}
      GUID: ${invDevice.deviceId}
      ${deviceLct}`;
  });

  const successfulInformation = deviceData.length
    ? `Successful Information:\n${deviceData.reduce((acc, device) => `${acc}${device}`)}`
    : '';

  if (data.xrinv.state === 'SUCCESS' && data.xritm.state === 'SUCCESS' && data.xrdlg.state === 'SUCCESS') {
    return successfulInformation;
  }

  const failScreenData = buildFailScreenData(data, msn);
  const failDataXrinv = failScreenData.xrinvText ? `${failScreenData.xrinvText}\n` : '';
  const failDataXritm = failScreenData.xritmText ? `${failScreenData.xritmText}\n` : '';
  const failDataXrdlg = failScreenData.xrdlgText ? `${failScreenData.xrdlgText}\n` : '';
  const failInformation = `${failScreenData.information}:\n${failDataXrinv}${failDataXritm}${failDataXrdlg}`;

  return `MPxN: ${mpxn}
            MSN: ${msn}
            ${chfText}
            ${failScreenData.xrinvText || failScreenData.xritmText || failScreenData.xrdlgText ? failInformation : ''}
            ${!(failScreenData.xrinvText && failScreenData.xritmText && failScreenData.xrdlgText) ? successfulInformation : ''}`;
}

export function buildInitialCommsCheckMessage(
  commsCheckResultContext: CommsCheckResult,
  mpxn: string,
  msn: string,
  chatReason: string,
) {
  const address = commsCheckResultContext.xrinv.address;
  return `Hi,
  
I can see you need assistance with the following: ${chatReason}

${
  address === null
    ? 'Address: Unable to retrieve address'
    : `Address:
      ${address}`
} 

${buildChatData(commsCheckResultContext, mpxn, msn)}

Please wait while we redirect you to an agent.
`;
}

export function buildFailScreenData(commsCheckResult: CommsCheckResult, msn: string) {
  const unableToDetermineAction =
    'Unable to determine action to take. Please check the MPxN and MSN and try again. If this issue persists, contact SST.';
  const commsHubRebootAction =
    'If this is the first unsuccessful response, please check the MPxN and MSN. If correct, perform a comms hub reboot and try again. If the issue persists, please contact SST.';
  const powerCycleAction =
    'If this is the first unsuccessful response, please check the MPxN and MSN. If correct, perform a power cycle and try again. If the issue persists, please contact SST.';
  const firstUnsuccessfulAction =
    'If this is the first unsuccessful response, please check the MPxN and MSN, and then try again. If the issue persists, contact SST.';
  const unableToRetrieveDevices = 'Unable to retrieve devices from the DCC';
  const unableToRetrieveReadings = `Unable to retrieve readings for MSN: ${msn}`;
  const unableToRetrieveLct = 'Unable to retrieve last communicated time for the devices';
  const lctNotActive = 'One or both meters have not communicated within the last 6 hours';
  const failInformationTitle = 'Fail Information';
  const timeoutInformationTitle = 'Timeout Information';
  const errorAction = "It's not you, it's us. Please check the MPxN and MSN, then retry. If the error persists, contact SST.";

  const filteredDevicesList = filterDevicesForEsmeGsme(commsCheckResult.xrinv.data);
  const isMetersActive = filteredDevicesList.every((device) => device.activeStatus === true);

  const result = determineCommsCheckFailResult(commsCheckResult, isMetersActive);
  let action;
  let xrinvText;
  let xritmText;
  let xrdlgText;
  let information;

  if (
    commsCheckResult.xrinv.state === 'UNEXPECTED_L1_ERROR' ||
    commsCheckResult.xritm.state === 'UNEXPECTED_L1_ERROR' ||
    commsCheckResult.xrdlg.state === 'UNEXPECTED_L1_ERROR'
  ) {
    action = errorAction;
  } else if (
    commsCheckResult.xrinv.state === 'UNEXPECTED_L2_ERROR' ||
    commsCheckResult.xritm.state === 'UNEXPECTED_L2_ERROR' ||
    commsCheckResult.xrdlg.state === 'UNEXPECTED_L2_ERROR'
  ) {
    action = errorAction;
  } else if (commsCheckResult.xrinv.state === 'FAIL' && commsCheckResult.xritm.state === 'FAIL') {
    action = unableToDetermineAction;
    xrinvText = unableToRetrieveDevices;
    xritmText = unableToRetrieveReadings;
    xrdlgText = unableToRetrieveLct;
    information = failInformationTitle;
  } else if (
    commsCheckResult.xrinv.state === 'SUCCESS' &&
    commsCheckResult.xritm.state === 'FAIL' &&
    commsCheckResult.xrdlg.state === 'FAIL'
  ) {
    action = commsHubRebootAction;
    xrinvText = null;
    xritmText = unableToRetrieveReadings;
    xrdlgText = unableToRetrieveLct;
    information = failInformationTitle;
  } else if (commsCheckResult.xrinv.state === 'FAIL' && commsCheckResult.xritm.state === 'SUCCESS') {
    action = unableToDetermineAction;
    xrinvText = unableToRetrieveDevices;
    xritmText = null;
    xrdlgText = unableToRetrieveLct;
    information = failInformationTitle;
  } else if (
    commsCheckResult.xrinv.state === 'SUCCESS' &&
    commsCheckResult.xritm.state === 'SUCCESS' &&
    commsCheckResult.xrdlg.state === 'FAIL'
  ) {
    action = firstUnsuccessfulAction;
    xrinvText = null;
    xritmText = null;
    xrdlgText = unableToRetrieveLct;
    information = failInformationTitle;
  } else if (
    commsCheckResult.xrinv.state === 'SUCCESS' &&
    commsCheckResult.xritm.state === 'FAIL' &&
    commsCheckResult.xrdlg.state === 'SUCCESS'
  ) {
    if (isMetersActive) {
      action = powerCycleAction;
      xrinvText = null;
      xritmText = unableToRetrieveReadings;
      xrdlgText = null;
      information = failInformationTitle;
    } else {
      action = commsHubRebootAction;
      xrinvText = null;
      xritmText = unableToRetrieveReadings;
      xrdlgText = lctNotActive;
      information = failInformationTitle;
    }
  } else if (commsCheckResult.xritm.state === 'SUCCESS' && commsCheckResult.xrdlg.state === 'SUCCESS' && !isMetersActive) {
    action = firstUnsuccessfulAction;
    xrinvText = null;
    xritmText = null;
    xrdlgText = lctNotActive;
    information = failInformationTitle;
    // TIMEOUT cases
  } else if (commsCheckResult.xrinv.state === 'PROCESSING' && commsCheckResult.xritm.state === 'PROCESSING') {
    action = unableToDetermineAction;
    xrinvText = unableToRetrieveDevices;
    xritmText = unableToRetrieveReadings;
    xrdlgText = unableToRetrieveLct;
    information = timeoutInformationTitle;
  } else if (commsCheckResult.xrinv.state === 'PROCESSING' && commsCheckResult.xritm.state === 'SUCCESS') {
    action = unableToDetermineAction;
    xrinvText = unableToRetrieveDevices;
    xritmText = null;
    xrdlgText = unableToRetrieveLct;
    information = timeoutInformationTitle;
  } else if (
    commsCheckResult.xrinv.state === 'SUCCESS' &&
    commsCheckResult.xritm.state === 'FAIL' &&
    commsCheckResult.xrdlg.state === 'PROCESSING'
  ) {
    action = commsHubRebootAction;
    xrinvText = null;
    xritmText = unableToRetrieveReadings;
    xrdlgText = unableToRetrieveLct;
    information = timeoutInformationTitle;
  } else if (commsCheckResult.xrinv.state === 'PROCESSING' && commsCheckResult.xritm.state === 'FAIL') {
    action = unableToDetermineAction;
    xrinvText = unableToRetrieveDevices;
    xritmText = unableToRetrieveReadings;
    xrdlgText = unableToRetrieveLct;
    information = timeoutInformationTitle;
  } else {
    action = firstUnsuccessfulAction;
    xrinvText = null;
    xritmText = null;
    xrdlgText = null;
    information = failInformationTitle;
  }

  return {
    result,
    action,
    xrinvText,
    xritmText,
    xrdlgText,
    information,
  };
}

export function determineCommsCheckFailResult(commsCheckResult: CommsCheckResult, isMetersActive: boolean = false): string {
  if (
    commsCheckResult.xritm.state === 'FAIL' ||
    (commsCheckResult.xrinv.state === 'SUCCESS' &&
      commsCheckResult.xritm.state === 'SUCCESS' &&
      commsCheckResult.xrdlg.state === 'FAIL') ||
    (commsCheckResult.xrinv.state === 'SUCCESS' &&
      commsCheckResult.xritm.state === 'SUCCESS' &&
      commsCheckResult.xrdlg.state === 'SUCCESS' &&
      !isMetersActive) ||
    (commsCheckResult.xrinv.state === 'SUCCESS' &&
      commsCheckResult.xritm.state === 'SUCCESS' &&
      commsCheckResult.xrdlg.state === 'PROCESSING')
  ) {
    return 'Not in comms';
  }
  return 'Unable to be determined';
}

export function determineCommsCheckResultForReporting(
  commsCheckResult: CommsCheckResult,
  isMetersActive: boolean = false,
): string {
  if (
    commsCheckResult.xrinv.state === 'PROXY_L1_ERROR' ||
    commsCheckResult.xritm.state === 'PROXY_L1_ERROR' ||
    commsCheckResult.xrdlg.state === 'PROXY_L1_ERROR'
  ) {
    return `Server error (XRINV: ${commsCheckResult.xrinv.state}, XRITM: ${commsCheckResult.xritm.state}, XRDLG: ${commsCheckResult.xrdlg.state})`;
  }
  if (
    commsCheckResult.xrinv.state === 'PROXY_L2_ERROR' ||
    commsCheckResult.xritm.state === 'PROXY_L2_ERROR' ||
    commsCheckResult.xrdlg.state === 'PROXY_L2_ERROR'
  ) {
    return `Server error (XRINV: ${commsCheckResult.xrinv.state}, XRITM: ${commsCheckResult.xritm.state}, XRDLG: ${commsCheckResult.xrdlg.state})`;
  }

  if (
    commsCheckResult.xrinv.state === 'UNEXPECTED_L1_ERROR' ||
    commsCheckResult.xritm.state === 'UNEXPECTED_L1_ERROR' ||
    commsCheckResult.xrdlg.state === 'UNEXPECTED_L1_ERROR'
  ) {
    return `Unexpected L1 error (XRINV: ${commsCheckResult.xrinv.state}, XRITM: ${commsCheckResult.xritm.state}, XRDLG: ${commsCheckResult.xrdlg.state})`;
  }
  if (
    commsCheckResult.xrinv.state === 'UNEXPECTED_L2_ERROR' ||
    commsCheckResult.xritm.state === 'UNEXPECTED_L2_ERROR' ||
    commsCheckResult.xrdlg.state === 'UNEXPECTED_L2_ERROR'
  ) {
    return `Unexpected L2 error (XRINV: ${commsCheckResult.xrinv.state}, XRITM: ${commsCheckResult.xritm.state}, XRDLG: ${commsCheckResult.xrdlg.state})`;
  }
  if (
    commsCheckResult.xrinv.state === 'SUCCESS' &&
    commsCheckResult.xritm.state === 'SUCCESS' &&
    commsCheckResult.xrdlg.state === 'SUCCESS' &&
    isMetersActive
  ) {
    return 'In Comms';
  }

  if (
    commsCheckResult.xritm.state === 'FAIL' ||
    (commsCheckResult.xrinv.state === 'SUCCESS' &&
      commsCheckResult.xritm.state === 'SUCCESS' &&
      commsCheckResult.xrdlg.state === 'FAIL') ||
    (commsCheckResult.xrinv.state === 'SUCCESS' &&
      commsCheckResult.xritm.state === 'SUCCESS' &&
      commsCheckResult.xrdlg.state === 'SUCCESS' &&
      !isMetersActive) ||
    (commsCheckResult.xrinv.state === 'SUCCESS' &&
      commsCheckResult.xritm.state === 'SUCCESS' &&
      commsCheckResult.xrdlg.state === 'PROCESSING')
  ) {
    return `Not in comms (XRINV: ${commsCheckResult.xrinv.state}, XRITM: ${commsCheckResult.xritm.state}, XRDLG: ${commsCheckResult.xrdlg.state})`;
  }

  // Generic case should show if any of the queries timeout/"PROCESSING"
  return `Unable to be determined (XRINV: ${commsCheckResult.xrinv.state}, XRITM: ${commsCheckResult.xritm.state}, XRDLG: ${commsCheckResult.xrdlg.state})`;
}
